// src/pages/salary/hooks/usePayrollData.js
import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../../../context/AuthContext';
import { useProfile } from '../../../hooks/useProfile';
import { buildCalculatedValues } from '../utils/taxCalculations';

const normalizeNumber = (value) => {
  const numericValue = Number(value ?? 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const normalizePayrollRows = (rows) => {
  if (!Array.isArray(rows)) return [];

  return rows.map((row) => ({
    ...row,
    amount: normalizeNumber(row?.amount),
  }));
};

const normalizePayrollData = (data) => {
  if (!data || typeof data !== 'object') return data;

  const numericFields = [
    'basicSalary',
    'grossSalary',
    'netSalary',
    'monthlyTax',
    'martyrsFund',
    'transportationAllowances',
    'transpotartionAllowances',
    'variableSalary',
    'overtimeValue',
    'overtimeHours',
    'bonusValue',
    'expenses',
    'achievement',
    'totalAllowances',
    'totalDeductions',
    'absenceDays',
    'absenceDaysValue',
    'penalityDays',
    'penalityDaysValue',
    'sickLeave25DeductionDays',
    'sickLeave25DeductionValue',
  ];

  const normalizedData = {
    ...data,
    allowances: normalizePayrollRows(data.allowances),
    deductions: normalizePayrollRows(data.deductions),
  };

  numericFields.forEach((field) => {
    normalizedData[field] = normalizeNumber(data[field]);
  });

  return normalizedData;
};

export const usePayrollData = ({ apiBaseUrl, t }) => {
  const { profile } = useProfile();
  const infinityCode             = profile?.infinityCode || localStorage.getItem('infinityCode') || '';
  const clientCode               = profile?.clientCode || localStorage.getItem('clientCode') || infinityCode;
  const arabicName               = profile?.employeeArabicName || localStorage.getItem('employeeArabicName') || localStorage.getItem('employeeName') || 'Employee';
  const netSalaryFromStorage     = parseFloat(profile?.netSalary)       || 0;
  const insuranceSalaryFromStorage = parseFloat(profile?.insuranceSalary) || netSalaryFromStorage;

  const [loading,          setLoading]         = useState(true);
  const [creating,         setCreating]        = useState(false);
  const [error,            setError]           = useState(null);
  const [payrollData,      setPayrollData]     = useState(null);
  const [selectedYear,     setSelectedYear]    = useState(new Date().getFullYear());
  const [selectedMonth,    setSelectedMonth]   = useState(new Date().getMonth() + 1);
  const [availableMonths,  setAvailableMonths] = useState([]);
  const [availableYears,   setAvailableYears]  = useState([]);

  const calculatedValues = useMemo(
    () => buildCalculatedValues(netSalaryFromStorage, insuranceSalaryFromStorage),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [netSalaryFromStorage, insuranceSalaryFromStorage, profile]
  );

  // fetch available years
  useEffect(() => {
    if (!infinityCode) return;
    apiFetch(`${apiBaseUrl}/payrollX/employee/${infinityCode}`, { method: 'GET' })
      .then((r) => r.json())
      .then((result) => {
        if (result.success && Array.isArray(result.data)) {
          const years = [...new Set(result.data.map((p) => p.year))].sort((a, b) => b - a);
          setAvailableYears(years);
        }
      })
      .catch((e) => console.error('Error fetching years:', e));
  }, [infinityCode, apiBaseUrl]);

  // fetch available months for selected year
  useEffect(() => {
    if (!infinityCode) return;
    apiFetch(`${apiBaseUrl}/payrollX/employee/${infinityCode}?year=${selectedYear}`, { method: 'GET' })
      .then((r) => r.json())
      .then((result) => {
        if (result.success && Array.isArray(result.data)) {
          const months = [...new Set(result.data.map((p) => p.month))].sort((a, b) => a - b);
          setAvailableMonths(months);
        }
      })
      .catch((e) => console.error('Error fetching months:', e));
  }, [infinityCode, selectedYear, apiBaseUrl]);

  // initialize payroll (fetch or create)
  useEffect(() => {
    if (!infinityCode || !calculatedValues) {
      setError(t.missingDataError);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const checkRes = await apiFetch(
          `${apiBaseUrl}/payrollX/employee/${infinityCode}?year=${selectedYear}&month=${selectedMonth}`,
          { method: 'GET' }
        );

        if (checkRes.ok) {
          const result = await checkRes.json();
          if (result.success && Array.isArray(result.data) && result.data.length > 0) {
            setPayrollData(normalizePayrollData(result.data[0]));
            return;
          }
        }

        setCreating(true);
        const createRes = await apiFetch(`${apiBaseUrl}/payrollX`, {
          method: 'POST',
          body: JSON.stringify({ infinityCode: String(infinityCode), year: selectedYear, month: selectedMonth }),
        });
        const createResult = await createRes.json();

        if (createResult.success) {
          setPayrollData(normalizePayrollData(Array.isArray(createResult.data) ? createResult.data[0] : createResult.data));
        } else {
          setError(createResult.error || 'Failed to create payroll');
        }
      } catch (err) {
        console.error('Error initializing payroll:', err);
        setError(err.message);
      } finally {
        setLoading(false);
        setCreating(false);
      }
    })();
  }, [infinityCode, selectedYear, selectedMonth, apiBaseUrl, calculatedValues, t.missingDataError]);

  return {
    infinityCode, clientCode, arabicName,
    loading, creating, error,
    payrollData, calculatedValues,
    selectedYear, setSelectedYear,
    selectedMonth, setSelectedMonth,
    availableMonths, availableYears,
  };
};